import React, { useState } from 'react';
import { getApiBaseUrl } from '../config/api';
import './CreateAgentsFromTQ.css';

const CreateAgentsFromTQ = ({ user, onShowNotification }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [agencies, setAgencies] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [createResults, setCreateResults] = useState(null);
  const API_BASE_URL = getApiBaseUrl();

  const searchTQ = async () => {
    setIsSearching(true);
    setCreateResults(null);
    setAgencies([]);
    setSelected(new Set());
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams();
      if (searchTerm.trim()) params.set('search', searchTerm.trim());
      const url = `${API_BASE_URL}/api/admin/tq/agencies${params.toString() ? `?${params}` : ''}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) {
        onShowNotification?.(data.error || 'Failed to fetch TQ agencies', 'error');
        return;
      }
      if (data.success && Array.isArray(data.agencies)) {
        setAgencies(data.agencies);
        if (data.agencies.length === 0) {
          onShowNotification?.('No active agencies found for this search.', 'info');
        }
      }
    } catch (err) {
      console.error(err);
      onShowNotification?.('Error querying TQ database. Check connection and try again.', 'error');
    } finally {
      setIsSearching(false);
    }
  };

  const toggleSelect = (index) => {
    const next = new Set(selected);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    setSelected(next);
  };

  const toggleSelectAll = () => {
    if (selected.size === agencies.length) setSelected(new Set());
    else setSelected(new Set(agencies.map((_, i) => i)));
  };

  const createSelected = async () => {
    const toCreate = agencies.filter((_, i) => selected.has(i));
    if (toCreate.length === 0) {
      onShowNotification?.('Select at least one agency to create.', 'warning');
      return;
    }
    setIsCreating(true);
    setCreateResults(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/api/admin/tq/agents/create`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ agencies: toCreate })
      });
      const data = await res.json();
      if (!res.ok) {
        onShowNotification?.(data.error || 'Failed to create agents', 'error');
        return;
      }
      if (data.success && Array.isArray(data.results)) {
        setCreateResults(data.results);
        const created = data.results.filter((r) => r.status === 'created');
        const skipped = data.results.filter((r) => r.status === 'skipped');
        const errors = data.results.filter((r) => r.status === 'error');
        if (created.length) {
          onShowNotification?.(`${created.length} agent(s) created. ${skipped.length} skipped. ${errors.length} error(s).`, 'success');
        } else if (errors.length) {
          onShowNotification?.(`No agents created. ${errors.length} error(s).`, 'error');
        } else {
          onShowNotification?.(`All selected already exist or were skipped.`, 'info');
        }
      }
    } catch (err) {
      console.error(err);
      onShowNotification?.('Error creating agents.', 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const formatDate = (d) => {
    if (d == null || d === '') return '—';
    if (typeof d === 'string') return d.slice(0, 10);
    return d;
  };

  return (
    <div className="create-agents-from-tq">
      <div className="tq-search-section">
        <h3>Query TQ (Oracle) database</h3>
        <p className="tq-help">
          Search active agencies by name. Leave empty to list all active agencies. Then select rows and create agent accounts in AgentsBima.
        </p>
        <p className="tq-required-note">
          <strong>Required for creation:</strong> valid email, Tax PIN (KRA PIN), and IRA number. Rows missing any of these will be skipped with a reason.
        </p>
        <div className="tq-search-row">
          <input
            type="text"
            className="tq-search-input"
            placeholder="e.g. WARFA or agency name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchTQ()}
          />
          <button type="button" className="tq-search-btn" onClick={searchTQ} disabled={isSearching}>
            {isSearching ? 'Searching…' : 'Search TQ'}
          </button>
        </div>
      </div>

      {agencies.length > 0 && (
        <div className="tq-results-section">
          <div className="tq-results-header">
            <h3>TQ agencies ({agencies.length})</h3>
            <button
              type="button"
              className="tq-create-btn"
              onClick={createSelected}
              disabled={isCreating || selected.size === 0}
            >
              {isCreating ? 'Creating…' : `Create ${selected.size} selected agent(s)`}
            </button>
          </div>
          <div className="tq-table-wrap">
            <table className="tq-agencies-table">
              <thead>
                <tr>
                  <th className="col-check">
                    <input
                      type="checkbox"
                      checked={agencies.length > 0 && selected.size === agencies.length}
                      onChange={toggleSelectAll}
                      aria-label="Select all"
                    />
                  </th>
                  <th>TQ Short Description</th>
                  <th>Agency Name / Full Names</th>
                  <th>IRA Number</th>
                  <th>ID Number</th>
                  <th>KRA PIN</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Date Licensed</th>
                </tr>
              </thead>
              <tbody>
                {agencies.map((row, i) => (
                  <tr key={i}>
                    <td className="col-check">
                      <input
                        type="checkbox"
                        checked={selected.has(i)}
                        onChange={() => toggleSelect(i)}
                        aria-label={`Select ${row.AGN_SHT_DESC || row.AGN_NAME}`}
                      />
                    </td>
                    <td>{row.AGN_SHT_DESC ?? '—'}</td>
                    <td>{row.AGN_NAME ?? '—'}</td>
                    <td>{row.AGN_REG_CODE ?? '—'}</td>
                    <td>{row.AGN_ID_NO ?? row.AGN_PIN ?? '—'}</td>
                    <td>{row.AGN_PIN ?? '—'}</td>
                    <td>{row.AGN_EMAIL_ADDRESS ?? '—'}</td>
                    <td>{row.AGN_TEL1 ?? '—'}</td>
                    <td>{formatDate(row.AGN_STATUS_EFF_DATE)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {createResults && createResults.length > 0 && (
        <div className="tq-create-results">
          <h3>Create results</h3>
          <ul className="tq-results-list">
            {createResults.map((r, i) => (
              <li key={i} className={`result-${r.status}`}>
                <strong>{r.short_description}</strong>
                {r.status === 'created' && (
                  <span className="result-credentials">
                    Username: <code>{r.username}</code> · Password: <code>{r.password}</code>
                    {r.emailSent === true && ' · Credentials sent by email'}
                    {r.emailSent === false && ' · Email not sent – share credentials manually'}
                    {r.note && ` · ${r.note}`}
                  </span>
                )}
                {r.status === 'skipped' && r.reason && <span> — {r.reason}</span>}
                {r.status === 'error' && r.reason && <span> — {r.reason}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CreateAgentsFromTQ;
